import { useCallback, useEffect, useRef, useState } from 'react';
import { BarChart3, Cookie, ShieldCheck, SlidersHorizontal, X } from 'lucide-react';
import {
  COOKIE_CONSENT_BROADCAST_CHANNEL,
  COOKIE_CONSENT_CHANGED_EVENT,
  COOKIE_SETTINGS_OPEN_EVENT,
  createCookieConsent,
  isCookieConsent,
  persistAndReadCookieConsent,
  readCookieConsent,
  sameCookieConsent,
  type CookieConsent as CookieConsentRecord,
} from './cookie-consent';

export interface CookieConsentProps {
  onConsentChange?(consent: CookieConsentRecord): void | Promise<void>;
  onStoragePreferenceChange?(enabled: boolean): void | Promise<void>;
  onAnalyticsPreferenceChange?(enabled: boolean): void | Promise<void>;
}

const focusableSelector = 'button:not([disabled]), input:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])';
const consentMatches = (left: CookieConsentRecord | null, right: CookieConsentRecord | null): boolean => (left === null && right === null) || sameCookieConsent(left, right);

export function CookieConsent({ onConsentChange, onStoragePreferenceChange, onAnalyticsPreferenceChange }: CookieConsentProps): React.JSX.Element | null {
  const [consent, setConsent] = useState<CookieConsentRecord | null>(() => readCookieConsent(document.cookie));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [preferenceDraft, setPreferenceDraft] = useState(consent?.preferences ?? false);
  const [analyticsDraft, setAnalyticsDraft] = useState(consent?.analytics ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const bannerTitleRef = useRef<HTMLElement>(null);
  const settingsDialogRef = useRef<HTMLDivElement>(null);
  const preferenceInputRef = useRef<HTMLInputElement>(null);
  const settingsTriggerRef = useRef<HTMLButtonElement>(null);
  const focusReturnRef = useRef<HTMLElement | null>(null);
  const consentRef = useRef(consent);
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const externalTransitionTailRef = useRef<Promise<void>>(Promise.resolve());
  consentRef.current = consent;

  const showBanner = consent === null && !settingsOpen;

  useEffect(() => {
    if (showBanner) bannerTitleRef.current?.focus({ preventScroll: true });
  }, [showBanner]);

  useEffect(() => {
    const open = (): void => {
      focusReturnRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setPreferenceDraft(consent?.preferences ?? false);
      setAnalyticsDraft(consent?.analytics ?? false);
      setSettingsOpen(true);
    };
    window.addEventListener(COOKIE_SETTINGS_OPEN_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_OPEN_EVENT, open);
  }, [consent]);

  const scheduleExternalConsent = useCallback((announced: CookieConsentRecord | null | undefined, explicitChoice: boolean): void => {
    const operation = externalTransitionTailRef.current.catch(() => undefined).then(async () => {
      let target = readCookieConsent(document.cookie);
      if (announced !== undefined && !consentMatches(target, announced)) return;
      let forceNecessaryClear = explicitChoice && target?.preferences === false;
      let appliedPreferences = consentRef.current?.preferences ?? false;
      let appliedAnalytics = consentRef.current?.analytics ?? false;
      setSaving(true);
      setSaveError(null);
      try {
        for (let attempt = 0; attempt < 3; attempt += 1) {
          const previous = consentRef.current;
          const targetPreferences = target?.preferences ?? false;
          const targetAnalytics = target?.analytics ?? false;
          if (targetPreferences && !appliedPreferences) {
            await onStoragePreferenceChange?.(true);
            appliedPreferences = true;
          } else if (!targetPreferences && (appliedPreferences || previous?.preferences === true || forceNecessaryClear)) {
            await onStoragePreferenceChange?.(false);
            appliedPreferences = false;
          }
          if (targetAnalytics !== appliedAnalytics) {
            await onAnalyticsPreferenceChange?.(targetAnalytics);
            appliedAnalytics = targetAnalytics;
          }

          const latest = readCookieConsent(document.cookie);
          if (!consentMatches(latest, target)) {
            target = latest;
            forceNecessaryClear = false;
            continue;
          }

          consentRef.current = target;
          setConsent(target);
          setPreferenceDraft(target?.preferences ?? false);
          setAnalyticsDraft(target?.analytics ?? false);
          if (target) {
            await onConsentChange?.(target);
            window.dispatchEvent(new CustomEvent<CookieConsentRecord>(COOKIE_CONSENT_CHANGED_EVENT, { detail: target }));
          }
          return;
        }
      } catch {
        // The runtime switches to memory before attempting strict deletion.
        // Keep the cookie truth visible while reporting incomplete cleanup.
        target = readCookieConsent(document.cookie);
        consentRef.current = target;
        setConsent(target);
        setPreferenceDraft(target?.preferences ?? false);
        setAnalyticsDraft(target?.analytics ?? false);
        setSettingsOpen(true);
        setSaveError('Настройка изменилась в другой вкладке, но локальные данные не удалось полностью очистить. Повторите «Только необходимые».');
      } finally {
        setSaving(false);
      }
    });
    externalTransitionTailRef.current = operation.catch(() => undefined);
  }, [onAnalyticsPreferenceChange, onConsentChange, onStoragePreferenceChange]);

  useEffect(() => {
    const reconcile = (): void => scheduleExternalConsent(undefined, false);
    const handleVisibility = (): void => { if (document.visibilityState === 'visible') reconcile(); };
    window.addEventListener('focus', reconcile);
    document.addEventListener('visibilitychange', handleVisibility);

    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(COOKIE_CONSENT_BROADCAST_CHANNEL);
    broadcastRef.current = channel;
    if (channel) channel.onmessage = (event: MessageEvent<unknown>): void => {
      if (isCookieConsent(event.data)) scheduleExternalConsent(event.data, true);
    };
    return () => {
      window.removeEventListener('focus', reconcile);
      document.removeEventListener('visibilitychange', handleVisibility);
      broadcastRef.current = null;
      channel?.close();
    };
  }, [scheduleExternalConsent]);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    preferenceInputRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSettingsOpen(false);
        window.setTimeout(() => focusReturnRef.current?.focus(), 0);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(settingsDialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [settingsOpen]);

  const commit = async (preferences: boolean, analytics: boolean): Promise<void> => {
    if (saving) return;
    const next = createCookieConsent(preferences, analytics);
    setSaving(true);
    setSaveError(null);
    try {
      const verified = persistAndReadCookieConsent(next);
      if (!sameCookieConsent(verified, next)) throw new Error('Cookie consent write was blocked');

      // Optional storage is enabled only after the consent cookie is readable.
      // Every explicit necessary-only action retries strict deletion, while passive
      // stored-false startup remains memory-only and never opens IndexedDB.
      if (!next.preferences || !next.analytics) broadcastRef.current?.postMessage(next);
      await onStoragePreferenceChange?.(next.preferences);
      await onAnalyticsPreferenceChange?.(next.analytics);

      const latest = readCookieConsent(document.cookie);
      if (!sameCookieConsent(latest, next)) {
        if ((latest?.preferences ?? false) !== next.preferences) await onStoragePreferenceChange?.(latest?.preferences ?? false);
        if ((latest?.analytics ?? false) !== next.analytics) await onAnalyticsPreferenceChange?.(latest?.analytics ?? false);
        scheduleExternalConsent(undefined, false);
        return;
      }

      consentRef.current = next;
      setConsent(next);
      setPreferenceDraft(preferences);
      setAnalyticsDraft(analytics);
      setSettingsOpen(false);
      await onConsentChange?.(next);
      window.dispatchEvent(new CustomEvent<CookieConsentRecord>(COOKIE_CONSENT_CHANGED_EVENT, { detail: next }));
      if (next.preferences || next.analytics) broadcastRef.current?.postMessage(next);
      window.setTimeout(() => settingsTriggerRef.current?.focus(), 0);
    } catch (error) {
      setSettingsOpen(true);
      setSaveError(error instanceof Error && error.message === 'Cookie consent write was blocked'
        ? 'Браузер не сохранил обязательную cookie с вашим выбором. Разрешите cookies этого сайта и повторите.'
        : 'Не удалось применить выбор или полностью очистить локальный кэш. Повторите действие.');
    } finally {
      setSaving(false);
    }
  };

  const openSettings = (trigger: HTMLElement): void => {
    focusReturnRef.current = trigger;
    setPreferenceDraft(consent?.preferences ?? false);
    setAnalyticsDraft(consent?.analytics ?? false);
    setSaveError(null);
    setSettingsOpen(true);
  };

  const closeSettings = (): void => {
    setSettingsOpen(false);
    window.setTimeout(() => focusReturnRef.current?.focus(), 0);
  };

  return <>
    {showBanner ? <section className="cookie-consent-banner" role="dialog" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-description">
      <div className="cookie-consent-icon" aria-hidden="true"><Cookie size={21} /></div>
      <div className="cookie-consent-copy">
        <strong ref={bannerTitleRef} id="cookie-banner-title" tabIndex={-1}>Мы используем cookies и локальные хранилища</strong>
        <p id="cookie-banner-description">Обязательные cookies и локальные хранилища обеспечивают вход, безопасность, запоминают этот выбор и защищают черновик авторизованного проекта. С разрешения мы также сохраним кэш рельефа и подключим Яндекс Метрику для анализа работы интерфейса.</p>
      </div>
      <div className="cookie-consent-actions">
        <button className="cookie-button decision" type="button" disabled={saving} onClick={() => void commit(true, true)}>Принять все</button>
        <button className="cookie-button decision" type="button" disabled={saving} onClick={() => void commit(false, false)}>Только необходимые</button>
        <button className="cookie-button text" type="button" onClick={(event) => openSettings(event.currentTarget)}>Настроить</button>
      </div>
    </section> : null}

    {consent && !settingsOpen ? <button ref={settingsTriggerRef} className="cookie-settings-trigger" type="button" aria-label="Открыть настройки cookies и локальных хранилищ" title="Настройки cookies и локальных хранилищ" onClick={(event) => openSettings(event.currentTarget)}><Cookie size={19} /><span>Cookies</span></button> : null}

    {settingsOpen ? <div className="cookie-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSettings(); }}>
      <div ref={settingsDialogRef} className="cookie-settings-dialog" role="dialog" aria-modal="true" aria-labelledby="cookie-settings-title" aria-describedby="cookie-settings-description">
        <header>
          <div><span className="cookie-settings-eyebrow">Конфиденциальность</span><h2 id="cookie-settings-title">Cookies и локальные хранилища</h2></div>
          <button className="cookie-dialog-close" type="button" aria-label="Закрыть настройки cookies и локальных хранилищ" onClick={closeSettings}><X size={20} /></button>
        </header>
        <p id="cookie-settings-description" className="cookie-settings-intro">Выберите, какие cookies и локальные хранилища сайт может использовать. Решение можно изменить в любое время.</p>

        <div className="cookie-category-list">
          <label className="cookie-category locked">
            <span className="cookie-category-icon" aria-hidden="true"><ShieldCheck size={19} /></span>
            <span className="cookie-category-copy"><strong>Необходимые</strong><small>Сессия входа, безопасность приложения, сохранение решения о cookies и аварийный черновик авторизованного проекта с объектами, координатами, настройками линков и компоновкой карты.</small></span>
            <span className="cookie-category-control"><input type="checkbox" checked disabled aria-label="Необходимые cookies всегда включены" /><i aria-hidden="true" /><b>Всегда</b></span>
          </label>

          <label className="cookie-category">
            <span className="cookie-category-icon" aria-hidden="true"><SlidersHorizontal size={19} /></span>
            <span className="cookie-category-copy"><strong>Настройки и производительность</strong><small>Разрешают хранить в IndexedDB кэш профилей рельефа на 7 дней: до 24 трасс и примерно 12 МБ. Это ускоряет повторные расчёты.</small></span>
            <span className="cookie-category-control"><input ref={preferenceInputRef} type="checkbox" checked={preferenceDraft} disabled={saving} onChange={(event) => setPreferenceDraft(event.target.checked)} aria-label="Разрешить локальный кэш для настроек и производительности" /><i aria-hidden="true" /><b>{preferenceDraft ? 'Включены' : 'Выключены'}</b></span>
          </label>

          <label className="cookie-category">
            <span className="cookie-category-icon" aria-hidden="true"><BarChart3 size={19} /></span>
            <span className="cookie-category-copy"><strong>Аналитика</strong><small>Разрешает Яндекс Метрике измерять посещаемость, переходы, клики и использование интерфейса, включая Вебвизор. Счётчик не загружается без этого разрешения.</small></span>
            <span className="cookie-category-control"><input type="checkbox" checked={analyticsDraft} disabled={saving} onChange={(event) => setAnalyticsDraft(event.target.checked)} aria-label="Разрешить Яндекс Метрику" /><i aria-hidden="true" /><b>{analyticsDraft ? 'Включена' : 'Выключена'}</b></span>
          </label>
        </div>

        <p className="cookie-no-tracking">Рекламные cookies порталом не используются. Разрешение на аналитику можно отозвать в любое время.</p>
        <p className="cookie-policy-links"><a href="/privacy.html" target="_blank" rel="noreferrer">Политика конфиденциальности</a><span aria-hidden="true">·</span><a href="/terms.html" target="_blank" rel="noreferrer">Условия использования</a></p>
        {saveError ? <p className="cookie-save-error" role="alert">{saveError}</p> : null}
        <details className="cookie-storage-details">
          <summary>Какие данные сохраняются</summary>
          <div className="cookie-storage-table">
            <p><strong>freedom_session</strong><span>Необходимая HttpOnly cookie входа</span><small>до 7 дней</small></p>
            <p><strong>freedom_cookie_consent</strong><span>Выбранные категории и дата решения</span><small>180 дней</small></p>
            <p><strong>Черновик проекта</strong><span>Объекты, координаты, параметры линков и компоновка карты вошедшего пользователя в localStorage</span><small>до очистки данных браузера</small></p>
            <p><strong>Кэш профилей рельефа</strong><span>Опциональное хранилище IndexedDB</span><small>до 7 дней</small></p>
            <p><strong>Яндекс Метрика</strong><span>Опциональные аналитические cookies и localStorage</span><small>по срокам Яндекс Метрики</small></p>
          </div>
        </details>
        <footer>
          <button className="cookie-button" type="button" disabled={saving} onClick={() => void commit(preferenceDraft, analyticsDraft)}>{saving ? 'Применение…' : 'Сохранить выбор'}</button>
          <button className="cookie-button decision" type="button" disabled={saving} onClick={() => void commit(false, false)}>Только необходимые</button>
          <button className="cookie-button decision" type="button" disabled={saving} onClick={() => void commit(true, true)}>Принять все</button>
        </footer>
      </div>
    </div> : null}
  </>;
}
